// src/components/TaskTemplatePicker.tsx
// Searchable picker for a TaskTemplate, in the same idiom as PipelinePicker:
// a trigger that reads the current choice, a portalled dropdown with a search
// box, and a clear affordance. Task admin surfaces pick a template's default
// this way instead of typing a UUID.
//
// Only active templates are offered. An inactive template cannot be applied, so
// offering one as a default would set up a default that silently does nothing.
//
// The trigger's `placeholder` is load-bearing here in a way it usually is not:
// callers that inherit a template pass the inherited value as the placeholder,
// so an unset field reads as what will actually happen rather than as "not set".

import { useState, useEffect, useRef, useMemo } from 'react';
import { useTaskTemplate, useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { TaskTemplate } from '@core/types/task';
import { AnchoredMenu } from './AnchoredMenu';

export interface TaskTemplatePickerProps {
  /** Selected template's entity id, or '' for none. */
  value: string;
  onChange: (templateId: string, template?: TaskTemplate) => void;
  label?: string;
  /** Shown when nothing is selected. */
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}

export function TaskTemplatePicker({
  value,
  onChange,
  label,
  placeholder,
  error,
  className = '',
  disabled,
  id,
}: TaskTemplatePickerProps) {
  const store = useTaskTemplate();
  const { t } = useI18n();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Lazy-load the first time the picker opens, then cache for the lifetime of
  // the component. The list is small enough not to need server-side search.
  useEffect(() => {
    if (!isOpen || loaded) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    store.ListTemplates({ pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [] })
      .then(() => {
        if (cancelled) return;
        setTemplates(store.items.filter((tpl) => tpl.is_active !== false));
        setLoaded(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, loaded, store]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [isOpen]);


  // Outside click, Escape and repositioning live in AnchoredMenu.

  const filtered = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter((tpl) =>
      (tpl.name || '').toLowerCase().includes(q) ||
      (tpl.description || '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  const selected = useMemo(
    () => (value ? templates.find((tpl) => tpl.entity?.id === value) : undefined),
    [value, templates],
  );

  const handleSelect = (template: TaskTemplate) => {
    onChange(template.entity?.id || '', template);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const clear = () => {
    onChange('', undefined);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground">
          {label}
        </label>
      )}

      {/* The clear control is a sibling of the trigger, not a child of it: a
          button inside a button is invalid markup and screen readers disagree
          about what to do with it. Positioned over the trigger's reserved
          right padding, so it looks inline and still tabs like a control. */}
      <div className="relative">
        <button
          id={id}
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`
            flex w-full items-center gap-2 rounded-md border py-2 pl-3 text-left text-sm transition-colors
            ${selected ? 'pr-16' : 'pr-9'}
            ${error ? 'border-destructive focus-visible:ring-destructive' : 'border-input focus-visible:ring-ring'}
            ${isOpen ? 'ring-2 ring-ring' : ''}
            bg-card text-foreground hover:bg-muted/50 focus:outline-none focus-visible:ring-2
            disabled:cursor-not-allowed disabled:opacity-50
          `}
        >
          <svg
            className="h-4 w-4 flex-shrink-0 text-muted-foreground"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          >
            <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>

          {selected ? (
            <span className="min-w-0 flex-1 truncate font-medium leading-tight text-foreground">
              {selected.name}
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {placeholder || t('task.admin.templatePicker.placeholder')}
            </span>
          )}
        </button>

        {selected && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={t('task.admin.templatePicker.clear')}
            className="absolute right-8 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}

        <svg
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}

      <AnchoredMenu
        anchor={buttonRef}
        ignoreRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        role="none"
        matchTriggerWidth
        minWidth={280}
        maxHeight={320}
        className="flex flex-col overflow-hidden"
      >
        <div className="flex-shrink-0 border-b border-border p-2">
          <div className="relative">
            <svg
              className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t('task.admin.templatePicker.searchPlaceholder')}
              className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder={t('task.admin.templatePicker.searchPlaceholder')}
            />
          </div>
        </div>

        <div className="overflow-y-auto py-1" style={{ maxHeight: 'calc(100% - 46px)' }}>
          {loading ? (
            <div className="px-3 py-4 text-center">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('task.admin.templatePicker.loading')}
              </p>
            </div>
          ) : loadError ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-destructive">{t('task.admin.templatePicker.loadError')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">
                {search ? t('common.noResults') : t('task.admin.templatePicker.noTemplates')}
              </p>
            </div>
          ) : (
            <>
              {value && (
                <button
                  type="button"
                  onClick={clear}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted focus:outline-none focus-visible:bg-muted"
                >
                  <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  <span className="text-sm text-muted-foreground">
                    {t('task.admin.templatePicker.clear')}
                  </span>
                </button>
              )}

              {filtered.map((tpl) => {
                const tplId = tpl.entity?.id || '';
                const isSelected = tplId === value;
                return (
                  <button
                    key={tplId}
                    type="button"
                    onClick={() => handleSelect(tpl)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors focus:outline-none focus-visible:bg-muted ${isSelected ? 'bg-primary/5' : 'hover:bg-muted'}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-foreground">{tpl.name}</span>
                        {isSelected && (
                          <svg className="h-4 w-4 flex-shrink-0 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        )}
                      </div>
                      {tpl.description && (
                        <p className="truncate text-xs text-muted-foreground">{tpl.description}</p>
                      )}
                    </div>
                    {tpl.auto_create && (
                      <Badge variant="secondary" size="sm" className="flex-shrink-0">
                        {t('task.admin.templatePicker.autoCreate')}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      </AnchoredMenu>
    </div>
  );
}

export default TaskTemplatePicker;
